// CommandBar — ⌘K global command palette.
// Keyboard: Ctrl/Cmd+K opens, arrow keys navigate, Enter executes, Escape closes.
// Sections: Navigate (workspaces), Opportunities (live recommendations), Actions.

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import {
  Search, Activity, BarChart2, Cpu, Settings,
  Zap, BriefcaseBusiness, TrendingUp, ArrowRight,
  Command, X,
} from 'lucide-react';
import { useOSStore } from '../store/useOSStore';
import { useAppStore } from '../store/useAppStore';

// ── Command definitions ───────────────────────────────────────────────────────

interface CommandItem {
  id: string;
  group: string;
  label: string;
  sub?: string;
  icon: React.ElementType;
  iconColor: string;
  action: () => void;
  keywords?: string;
}

// ── CommandBar ────────────────────────────────────────────────────────────────

export function CommandBar() {
  const navigate   = useNavigate();
  const { commandBarOpen, closeCommandBar } = useOSStore();
  const recommendations = useAppStore((s) => s.recommendations);

  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef  = useRef<HTMLInputElement>(null);
  const listRef   = useRef<HTMLDivElement>(null);

  // Reset on open/close
  useEffect(() => {
    if (commandBarOpen) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [commandBarOpen]);

  // Global keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        commandBarOpen ? closeCommandBar() : useOSStore.getState().openCommandBar();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [commandBarOpen, closeCommandBar]);

  const go = useCallback((to: string) => {
    closeCommandBar();
    void navigate(to);
  }, [closeCommandBar, navigate]);

  // Build static commands
  const staticCommands: CommandItem[] = useMemo(() => [
    {
      id: 'nav-pulse',
      group: 'Navigate',
      label: 'Pulse Command',
      sub: 'Cross-sport intelligence OS',
      icon: Activity,
      iconColor: '#a78bfa',
      action: () => go('/pulse/dashboard'),
      keywords: 'home pulse opportunities',
    },
    {
      id: 'nav-diamond',
      group: 'Navigate',
      label: 'DiamondEdge',
      sub: 'MLB intelligence workspace',
      icon: () => <span className="text-sm leading-none">⚾</span>,
      iconColor: '#3b82f6',
      action: () => go('/baseball/mlb/command-center'),
      keywords: 'mlb baseball diamond',
    },
    {
      id: 'nav-court',
      group: 'Navigate',
      label: 'CourtEdge',
      sub: 'WNBA intelligence workspace',
      icon: () => <span className="text-sm leading-none">🏀</span>,
      iconColor: '#f59e0b',
      action: () => go('/basketball/wnba/command-center'),
      keywords: 'wnba basketball court',
    },
    {
      id: 'nav-portfolio',
      group: 'Navigate',
      label: 'Portfolio',
      sub: 'Bankroll, bets, and analytics',
      icon: BriefcaseBusiness,
      iconColor: '#d97706',
      action: () => go('/portfolio'),
      keywords: 'portfolio bankroll bets exposure',
    },
    {
      id: 'nav-observatory',
      group: 'Navigate',
      label: 'Agent Observatory',
      sub: 'Orchestration, health, telemetry',
      icon: Cpu,
      iconColor: '#06b6d4',
      action: () => go('/observatory'),
      keywords: 'observatory agents health pipeline',
    },
    {
      id: 'nav-settings',
      group: 'Navigate',
      label: 'Settings',
      sub: 'Profile, billing, notifications',
      icon: Settings,
      iconColor: '#6b7280',
      action: () => go('/settings/profile'),
      keywords: 'settings profile account billing',
    },
    {
      id: 'action-opportunities',
      group: 'Actions',
      label: 'View All Opportunities',
      sub: 'Open cross-sport opportunity stream',
      icon: TrendingUp,
      iconColor: '#10b981',
      action: () => go('/pulse/picks'),
      keywords: 'opportunities picks recommendations',
    },
    {
      id: 'action-advisor',
      group: 'Actions',
      label: 'MLB Advisor',
      sub: 'Run intelligent game analysis',
      icon: BarChart2,
      iconColor: '#3b82f6',
      action: () => go('/baseball/mlb/advisor'),
      keywords: 'advisor analysis mlb game',
    },
  ], [go]);

  // Build recommendation commands from live data
  const recCommands: CommandItem[] = useMemo(() => {
    return recommendations
      .filter((r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET')
      .slice(0, 5)
      .map((r) => ({
        id: `rec-${r.id}`,
        group: 'Live Opportunities',
        label: `${r.awayTeam} @ ${r.homeTeam}`,
        sub: `${r.market.toUpperCase()} · ${r.selection} · ${r.decisionStatus === 'STRONG_BET' ? '🔥 STRONG' : 'QUALIFIED'}`,
        icon: Zap,
        iconColor: r.decisionStatus === 'STRONG_BET' ? '#d97706' : '#3b82f6',
        action: () => {
          closeCommandBar();
          const leaguePath = r.leagueId === 'wnba' ? '/basketball/wnba' : '/baseball/mlb';
          void navigate(`${leaguePath}/game/${r.eventPk}`);
        },
        keywords: `${r.awayTeam} ${r.homeTeam} ${r.leagueId} ${r.market}`.toLowerCase(),
      }));
  }, [recommendations, closeCommandBar, navigate]);

  const allCommands = useMemo(() => [...recCommands, ...staticCommands], [recCommands, staticCommands]);

  // Filter by query
  const filtered = useMemo(() => {
    if (!query.trim()) return allCommands;
    const q = query.toLowerCase();
    return allCommands.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.sub?.toLowerCase().includes(q) ||
        c.group.toLowerCase().includes(q) ||
        c.keywords?.includes(q),
    );
  }, [allCommands, query]);

  // Group filtered items
  const groups = useMemo(() => {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const c of filtered) {
      if (!seen.has(c.group)) {
        seen.add(c.group);
        order.push(c.group);
      }
    }
    return order;
  }, [filtered]);

  // Clamp active index
  useEffect(() => {
    setActiveIdx((i) => Math.min(i, Math.max(filtered.length - 1, 0)));
  }, [filtered.length]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      filtered[activeIdx]?.action();
    } else if (e.key === 'Escape') {
      closeCommandBar();
    }
  };

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  return (
    <AnimatePresence>
      {commandBarOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            onClick={closeCommandBar}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -12 }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            className="fixed top-[15vh] left-1/2 -translate-x-1/2 z-[61] w-full max-w-lg"
          >
            <div className="bg-surface-1 border border-surface-4 rounded-xl shadow-2xl overflow-hidden">

              {/* Search input */}
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-surface-4">
                <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                  onKeyDown={handleKeyDown}
                  placeholder="Search commands, games, workspaces…"
                  className="flex-1 bg-transparent text-sm text-gray-100 placeholder-gray-600 outline-none"
                  autoComplete="off"
                  spellCheck={false}
                />
                {query && (
                  <button onClick={() => setQuery('')} className="text-gray-600 hover:text-gray-400 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <div className="flex items-center gap-0.5 flex-shrink-0 text-gray-600">
                  <kbd className="flex items-center justify-center w-5 h-5 rounded bg-surface-3 text-[10px] font-mono">
                    <Command className="w-2.5 h-2.5" />
                  </kbd>
                  <kbd className="flex items-center justify-center w-5 h-5 rounded bg-surface-3 text-[10px] font-mono">K</kbd>
                </div>
              </div>

              {/* Results */}
              <div ref={listRef} className="overflow-y-auto max-h-[360px] py-1">
                {filtered.length === 0 ? (
                  <p className="text-center text-xs text-gray-600 py-8">No results for "{query}"</p>
                ) : (
                  groups.map((group) => {
                    const items = filtered.filter((c) => c.group === group);
                    const groupStartIdx = filtered.indexOf(items[0]);
                    return (
                      <div key={group}>
                        <p className="px-3 pt-2 pb-0.5 text-[9px] font-semibold uppercase tracking-widest text-gray-600">
                          {group}
                        </p>
                        {items.map((cmd) => {
                          const idx = filtered.indexOf(cmd);
                          const isActive = idx === activeIdx;
                          const Icon = cmd.icon;
                          return (
                            <button
                              key={cmd.id}
                              data-idx={idx}
                              onMouseEnter={() => setActiveIdx(idx)}
                              onClick={cmd.action}
                              className={clsx(
                                'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
                                isActive ? 'bg-surface-3' : 'hover:bg-surface-2',
                              )}
                            >
                              {/* Icon */}
                              <div
                                className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border"
                                style={{
                                  backgroundColor: `${cmd.iconColor}15`,
                                  borderColor: `${cmd.iconColor}30`,
                                }}
                              >
                                <Icon
                                  className="w-3.5 h-3.5"
                                  style={{ color: cmd.iconColor }}
                                />
                              </div>

                              {/* Text */}
                              <div className="flex-1 min-w-0">
                                <p className={clsx('text-sm leading-tight', isActive ? 'text-gray-100' : 'text-gray-300')}>
                                  {cmd.label}
                                </p>
                                {cmd.sub && (
                                  <p className="text-[10px] text-gray-600 leading-tight mt-0.5 truncate">
                                    {cmd.sub}
                                  </p>
                                )}
                              </div>

                              {/* Arrow hint when active */}
                              {isActive && (
                                <ArrowRight className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="px-3 py-1.5 border-t border-surface-4 flex items-center gap-3 text-[9px] text-gray-700">
                <span><kbd className="font-mono bg-surface-3 px-1 py-0.5 rounded">↑↓</kbd> navigate</span>
                <span><kbd className="font-mono bg-surface-3 px-1 py-0.5 rounded">↵</kbd> select</span>
                <span><kbd className="font-mono bg-surface-3 px-1 py-0.5 rounded">esc</kbd> close</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
